import React, {Fragment} from "react";
import {formatAgeShort, formatDateTime, formatGenderShort, formatPatientName} from "../../../helpers/FormatHelper";
import {Record} from '../../../models/Record';
import {StudyIcon, StudyType} from "../StudyIcon/StudyIcon";
import {useTranslation} from "react-i18next";
import {useLongPress} from "../../../hooks";
import {getUserPhotoUrl} from "../../../api/ApiHelper";
import {Gender} from "../../../models/Gender";
import {Avatar} from "@mui/material";

interface RecordProps {
    record: Record;
    onClick: () => void;
    onContextMenuHandler: (anchorX: number, anchorY: number) => void;
    onStudyClick: (type: StudyType) => void;
    onStudyContextMenu?: (type: StudyType, anchorX: number, anchorY: number) => void;
    onUserClick?: (userId: string) => void;
    compact?: boolean;
}

export const RecordsLong: React.FC<RecordProps> = ({ record, onClick, onContextMenuHandler, onStudyClick, onStudyContextMenu, onUserClick, compact = false }: RecordProps) => {
    const {t} = useTranslation();
    const contextMenuHandler = (event : any) => {
        event.preventDefault();
        onContextMenuHandler(event.clientX, event.clientY);
    };
    const userClickHandler = (e : React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (onUserClick && record.ownerInfo?.ownerId){
            onUserClick(record.ownerInfo.ownerId);
        }
    };
    const genderAge = `${record.gender !== Gender.Undefined ? `${formatGenderShort(t, record.gender)} · ` : ""}${formatAgeShort(record.age)}`;
    // touch: long press opens the context menu (iOS does not fire contextmenu)
    const longPress = useLongPress(onContextMenuHandler);
    return (
        <tr onClick={onClick} onContextMenu={contextMenuHandler} {...longPress}>
            {record.ownerInfo &&
                <td align="left" className="d-flex justify-content-start align-items-center text-left">
                    <Avatar className="user-avatar-img mx-2" alt={record.ownerInfo.ownerName} src={getUserPhotoUrl(record.ownerInfo.ownerPhotoUrl)}
                            onClick={compact ? userClickHandler : undefined}/>
                    {!compact &&
                        <div className={`text-tooltip ${onUserClick ? "clickable" : ""}`} onClick={userClickHandler}>{record.ownerInfo.ownerName}<span>{record.ownerInfo.ownerEmail}</span></div>
                    }
                </td>
            }
            <td align="center" className={`text-left ${compact ? "list-cell-wrap" : ""}`}>
                {formatPatientName(t, record.patientId === null, record.patientName)}
                {compact &&
                    <Fragment>
                        <div className="list-cell-subtitle">{genderAge}</div>
                        <div className="list-cell-subtitle">{formatDateTime(record.dateTime)}</div>
                    </Fragment>
                }
            </td>
            {!compact && <td align="center" className="text-center">{genderAge}</td>}
            {!compact && <td align="center">{formatDateTime(record.dateTime)}</td>}
            <td align="center">
                <div className="d-flex align-items-center justify-content-center">
                    <StudyIcon type={StudyType.State} hasText={!compact} isVisible={record.state !== null} onClick={() => onStudyClick(StudyType.State)}/>
                </div>
            </td>
            <td align="center">
                <div className="d-flex align-items-center justify-content-center">
                    <StudyIcon type={StudyType.Ecg} hasText={!compact} isVisible={record.ecgStudy !== null} onClick={() => onStudyClick(StudyType.Ecg)}
                               onContextMenu={onStudyContextMenu ? (x, y) => onStudyContextMenu(StudyType.Ecg, x, y) : undefined}/>
                </div>
            </td>
            <td align="center">
                <div className="d-flex align-items-center justify-content-center">
                    <StudyIcon type={StudyType.Spiro} hasText={!compact} isVisible={record.spiroStudy !== null} onClick={() => onStudyClick(StudyType.Spiro)}
                               onContextMenu={onStudyContextMenu ? (x, y) => onStudyContextMenu(StudyType.Spiro, x, y) : undefined}/>
                </div>
            </td>
            <td align="center">
                <div className="d-flex align-items-center justify-content-center">
                    <StudyIcon type={StudyType.Stethoscope} hasText={!compact} isVisible={record.stethoscopeStudy !== null} onClick={() => onStudyClick(StudyType.Stethoscope)}
                               onContextMenu={onStudyContextMenu ? (x, y) => onStudyContextMenu(StudyType.Stethoscope, x, y) : undefined}/>
                </div>
            </td>
            <td align="center">
                <div className="d-flex align-items-center justify-content-center">
                    <StudyIcon type={StudyType.LtEcg} hasText={!compact} isVisible={record.ltEcgStudy !== null} onClick={() => onStudyClick(StudyType.LtEcg)}/>
                </div>
            </td>
        </tr>
    );
}